<template>
  <div class="fairness-dashboard" :class="{ collapsed: !open, 'fill-available': fillAvailable && open }">
    <button
      type="button"
      class="fairness-head"
      :aria-expanded="open"
      @click="toggleOpen"
    >
      <span class="head-chevron" aria-hidden="true">▾</span>
      <span class="head-title">Fairness check</span>
      <span class="head-summary" v-if="totalCells > 0 && !open">
        {{ partyAName || 'Party A' }} {{ formatCount(aShare) }} · {{ partyBName || 'Party B' }} {{ formatCount(bShare) }}
      </span>
    </button>

    <div v-show="open" class="fairness-body">
      <p class="head-desc">
        Totals across the next {{ years.length }} years, plus who has
        {{ kidsLabel }} on every holiday in each year.
        Counts the {{ holidays.length }} holidays as equal units, not as actual
        days (spring break and Christmas Day each count as one).
      </p>

      <!-- Totals: headline numbers + horizontal split bar. -->
      <div class="totals">
      <div class="totals-line">
        <span class="t-label t-a">
          <span class="dot dot-a"></span>
          <strong>{{ partyAName || 'Party A' }}</strong>: {{ formatCount(aShare) }}
        </span>
        <span class="t-label t-b">
          <span class="dot dot-b"></span>
          <strong>{{ partyBName || 'Party B' }}</strong>: {{ formatCount(bShare) }}
        </span>
        <span v-if="totals.shared" class="t-label t-aside">
          <span class="dot dot-shared"></span>{{ totals.shared }} shared
        </span>
        <span v-if="totals.base" class="t-label t-aside">
          <span class="dot dot-base"></span>{{ totals.base }} per regular schedule
        </span>
        <span v-if="totals.flex" class="t-label t-aside">
          <span class="dot dot-flex"></span>{{ totals.flex }} to be agreed
        </span>
        <span v-if="totals.unset" class="t-label t-aside">
          <span class="dot dot-unset"></span>{{ totals.unset }} still unset
        </span>
      </div>
      <div class="bar" v-if="totalCells > 0">
        <div v-if="totals.A"      class="seg seg-a"      :style="{ width: pct(totals.A) + '%' }" :title="`${formatCount(totals.A)} with ${partyAName || 'Party A'}`"></div>
        <div v-if="totals.B"      class="seg seg-b"      :style="{ width: pct(totals.B) + '%' }" :title="`${formatCount(totals.B)} with ${partyBName || 'Party B'}`"></div>
        <div v-if="totals.shared" class="seg seg-shared" :style="{ width: pct(totals.shared) + '%' }" title="Shared / split"></div>
        <div v-if="totals.base"   class="seg seg-base"   :style="{ width: pct(totals.base) + '%' }" title="Per regular schedule"></div>
        <div v-if="totals.flex"   class="seg seg-flex"   :style="{ width: pct(totals.flex) + '%' }" title="To be agreed"></div>
        <div v-if="totals.unset"  class="seg seg-unset"  :style="{ width: pct(totals.unset) + '%' }" title="Still unset"></div>
      </div>
      <p v-else class="empty-bar">Configure rules in the editor to see the fairness split.</p>
    </div>

    <!-- Imbalance drivers callout: only when spread is meaningful. -->
    <p v-if="drivers.length" class="drivers">
      Most of the spread comes from
      <strong>{{ driversList }}</strong>
      always landing with {{ dominantName }}. Consider whether any of these could alternate instead.
    </p>

    <!-- Full holiday × year breakdown — replaces the old separate Sanity Check
         table. Shows every configured holiday across the 3-year window so the
         parties can spot specific issues. -->
    <div v-if="holidays.length" class="rotation-table-wrap">
      <table class="rotation-table">
        <thead>
          <tr>
            <th>Holiday</th>
            <th v-for="y in years" :key="y">{{ y }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in holidays" :key="h.id">
            <td class="cell-name">{{ h.name }}</td>
            <td v-for="y in years" :key="y" :class="cellClass(h.rule, y)">
              {{ cellLabel(h.rule, y) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

      <!-- Legend matches the cell colors above + the bar segments above. -->
      <div class="legend">
        <span class="swatch swatch-a"></span><span>{{ partyAName || 'Party A' }}</span>
        <span class="swatch swatch-b"></span><span>{{ partyBName || 'Party B' }}</span>
        <span class="swatch swatch-share"></span><span>Shared / split</span>
        <span class="swatch swatch-base"></span><span>Per regular schedule</span>
        <span class="swatch swatch-flex"></span><span>To be agreed</span>
        <span class="swatch swatch-pending"></span><span>Needs decision</span>
      </div>
    </div>
  </div>
</template>

<script>
import { parentForYear } from '../proseRenderer.js'
import { computeFairness } from '../fairness.js'

export default {
  name: 'FairnessDashboard',
  emits: ['toggle-open'],
  props: {
    holidays: { type: Array, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    fillAvailable: { type: Boolean, default: false },
  },
  data() {
    // Collapsed by default so it doesn't add scroll-distance when the user
    // is mainly working in the editor. The collapsed header still shows
    // the headline split so a quick glance is available without expanding.
    return { open: false }
  },
  mounted() {
    this.$emit('toggle-open', this.open)
  },
  computed: {
    years() {
      const start = new Date().getFullYear()
      // Three-year window: enough to see alternating patterns flip and to
      // sanity-check year-by-year resolution; short enough that the table
      // stays scannable in the side panel.
      return [start, start + 1, start + 2]
    },
    kidsLabel() {
      if (Array.isArray(this.children) && this.children.length === 1) {
        return this.children[0]?.name || 'the child'
      }
      if (Array.isArray(this.children) && this.children.length >= 2) {
        return 'the Children'
      }
      return 'the children'
    },
    fairness() {
      return computeFairness(this.holidays, this.years)
    },
    totals()   { return this.fairness.totals },
    aShare()   { return this.fairness.aShare },
    bShare()   { return this.fairness.bShare },
    drivers()  { return this.fairness.drivers },
    dominant() { return this.fairness.dominant },
    dominantName() {
      if (this.dominant === 'A') return this.partyAName || 'Party A'
      if (this.dominant === 'B') return this.partyBName || 'Party B'
      return ''
    },
    driversList() {
      const names = this.drivers.map((d) => d.name)
      if (names.length <= 1) return names[0] || ''
      if (names.length === 2) return `${names[0]} and ${names[1]}`
      return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
    },
    totalCells() {
      const t = this.totals
      return t.A + t.B + t.shared + t.base + t.flex + t.unset
    },
  },
  methods: {
    toggleOpen() {
      this.open = !this.open
      this.$emit('toggle-open', this.open)
    },
    pct(n) {
      if (this.totalCells === 0) return 0
      return (n / this.totalCells) * 100
    },
    formatCount(n) {
      // Half-credits from "shared" rules can produce .5 values; round to one
      // decimal so the headline stays tidy.
      return Number.isInteger(n) ? String(n) : n.toFixed(1)
    },
    cellLabel(rule, year) {
      const who = parentForYear(rule, year)
      if (who === 'A') return this.partyAName || 'Party A'
      if (who === 'B') return this.partyBName || 'Party B'
      if (who === 'shared') return 'Both'
      if (who === 'split') return 'Split'
      if (who === 'rotation') return 'Wk on/off'
      if (who === 'base') return 'Base'
      if (who === 'flex') return 'TBD'
      return ''
    },
    cellClass(rule, year) {
      const who = parentForYear(rule, year)
      if (who === 'A') return 'cell-a'
      if (who === 'B') return 'cell-b'
      if (who === 'shared' || who === 'split' || who === 'rotation') return 'cell-share'
      if (who === 'base') return 'cell-base'
      if (who === 'flex') return 'cell-flex'
      return 'cell-pending'
    },
  },
}
</script>

<style scoped>
.fairness-dashboard {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  transition: padding 0.15s;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.fairness-dashboard:not(.collapsed) {
  padding: 1rem 1.1rem;
  max-height: min(42vh, 440px);
}
.fairness-dashboard.fill-available {
  max-height: none;
  height: 100%;
  width: 100%;
}
.fairness-dashboard.fill-available .fairness-body {
  flex: 1;
}

/* Accordion header — clickable; matches the category accordion pattern. */
.fairness-head {
  width: 100%;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  padding: 0.35rem 0.25rem;
  border-radius: 6px;
  transition: background 0.15s;
  flex-shrink: 0;
}
.fairness-head:hover { background: rgba(58,100,112,0.06); }
.head-chevron {
  color: #3f6772;
  font-size: 0.8rem;
  transition: transform 0.2s;
  display: inline-block;
}
.fairness-dashboard.collapsed .head-chevron { transform: rotate(-90deg); }
.head-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #1e293b;
  flex-shrink: 0;
}
.head-summary {
  font-size: 0.78rem;
  color: #64748b;
  flex-grow: 1;
  text-align: right;
}
.head-desc {
  margin: 0 0 0.85rem;
  font-size: 0.78rem;
  color: #64748b;
  max-width: 65ch;
}
.fairness-body {
  padding: 0.5rem 0.25rem 0.1rem;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.totals { margin-bottom: 0.85rem; }
.totals-line {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  align-items: baseline;
  margin-bottom: 0.55rem;
  font-size: 0.82rem;
  color: #475569;
}
.t-label { display: inline-flex; align-items: center; gap: 0.35rem; }
.t-label strong { color: #1e293b; font-weight: 600; }
.t-a strong { color: #3f6772; }
.t-b strong { color: #85581a; }
.t-aside { color: #64748b; font-size: 0.78rem; }

.dot {
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex-shrink: 0;
}
.dot-a      { background: #3f6772; }
.dot-b      { background: #85581a; }
.dot-shared { background: #557d89; }
.dot-base   { background: #94a3b8; }
.dot-flex   { background: #6b21a8; }
.dot-unset  { background: #cbd5e1; }

.bar {
  display: flex;
  height: 14px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}
.seg { height: 100%; transition: width 0.3s ease; }
.seg-a      { background: #3f6772; }
.seg-b      { background: #85581a; }
.seg-shared { background: #557d89; }
.seg-base   { background: #94a3b8; }
.seg-flex   { background: #6b21a8; }
.seg-unset  { background: #cbd5e1; }
.empty-bar {
  margin: 0;
  padding: 0.5rem 0.75rem;
  font-size: 0.78rem;
  color: #94a3b8;
  font-style: italic;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  text-align: center;
}

.drivers {
  margin: 0 0 0.85rem;
  padding: 0.5rem 0.85rem;
  background: #fef6e0;
  border-left: 3px solid #f7a91b;
  border-radius: 0 6px 6px 0;
  font-size: 0.8rem;
  color: #85581a;
}
.drivers strong { color: #85581a; }

/* Full holiday × year resolution table — same data the old "Sanity Check"
   showed, now folded into this single panel so parents see totals AND the
   per-row breakdown in one place. */
.rotation-table-wrap {
  overflow-x: auto;
  margin-bottom: 0.55rem;
}
.rotation-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.rotation-table th,
.rotation-table td {
  text-align: left;
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid #f1f5f9;
}
.rotation-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.rotation-table .cell-name {
  color: #1e293b;
  font-weight: 500;
}

.cell-a { color: #3f6772; background: #eef3f4; }
.cell-b { color: #85581a; background: #fef6e0; }
.cell-share { color: #557d89; background: #eef3f4; }
.cell-base { color: #475569; background: #f8fafc; font-style: italic; }
.cell-flex { color: #6b21a8; background: #faf5ff; font-style: italic; }
.cell-pending { color: #94a3b8; }

.legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.85rem;
  margin-top: 0.4rem;
  font-size: 0.75rem;
  color: #64748b;
}
.swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  margin-right: 0.25rem;
  vertical-align: middle;
}
.swatch-a       { background: #eef3f4; border: 1px solid #3f6772; }
.swatch-b       { background: #fef6e0; border: 1px solid #85581a; }
.swatch-share   { background: #eef3f4; border: 1px solid #557d89; }
.swatch-base    { background: #f8fafc; border: 1px solid #475569; }
.swatch-flex    { background: #faf5ff; border: 1px solid #6b21a8; }
.swatch-pending { background: #fff;    border: 1px solid #cbd5e1; }

/* Don't print the dashboard — it's a screen-only review tool.
   The PDF still renders the agreement clauses + at-a-glance table only. */
@media print {
  .fairness-dashboard { display: none !important; }
}
</style>
